import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";

import { useToast } from "../../context/ToastContext";
import LineApi from "../../services/LineApi";
import FamilyApi from "../../services/FamilyApi";
import { PLANT_ID } from "../../utilities/util";

// Design System
import FormCard from "../../components/FormCard/FormCard";
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

interface FamilyRow {
  id: string;
  name: string;
  description?: string;
  code?: number;
  plantId?: string | null;
}

const AssignFamiliesToLine: React.FC = () => {
  const { lineId } = useParams<{ lineId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["form", "general"]);
  const { displayToast } = useToast();

  const [lineName, setLineName] = useState<string>("");
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const [selectedFamilies, setSelectedFamilies] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc"|"desc">("asc");
  const [savedFilters, setSavedFilters] = useState<Record<string, any>>({});

  // Filter states
  const [filterName, setFilterName] = useState("");
  const [filterCode, setFilterCode] = useState<number | null>(null);
  const [filterDescription, setFilterDescription] = useState("");
  const plantId = localStorage.getItem(PLANT_ID) || "";

  useEffect(() => {
    if (!lineId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const loadData: any = async () => {
    setLoading(true);
    try {
      try {
        const resp = await LineApi.GetLineById(lineId!);
        setLineName(resp?.data?.name ?? resp?.data?.Name ?? "");
      } catch {
      }
      const linkedResp = await FamilyApi.GetFamiliesByLine(lineId!);
      const linked = Array.isArray(linkedResp?.data)
        ? linkedResp.data
        : linkedResp?.data?.Families ?? [];
      const linkedIds = linked.map((f: FamilyRow) => f.id);
      setInitialSelectedIds(linkedIds);
      setSelectedFamilies(linked);
      fetchFamilies(savedFilters, page, sortField, sortOrder, rowsPerPage);
    } catch (err: any) {
      displayToast("error", t("form:error") as string, err?.message ?? "Error", false, 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies: any = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc"|"desc" = sortOrder,
    pageSize: number = rowsPerPage
  ) => {
    setLoading(true);
    try {
      const name = filters.name?.value || undefined;
      const code = filters.code?.value || undefined;
      const description = filters.description?.value || undefined;
      const response = await FamilyApi.GetFamiliesByFilterAsync(
        [plantId],
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        code,
        name,
        description
      );
      const data = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.families)
        ? response.data.families
        : [];
      setFamilies(data);
      setTotalRecords(response.data.total || data.length);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      code: filterCode !== null ? { value: filterCode } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
    };

    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    fetchFamilies(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterCode(null);
    setFilterDescription("");

    setPage(0);
    setSavedFilters({});
    fetchFamilies({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRowsPerPage(e.rows);
    fetchFamilies(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const handleSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField || "");
    setSortOrder(dir);
    fetchFamilies(savedFilters, page, e.sortField || "", dir);
  };

  const handleCancel: any = () => {
    navigate(-1);
  };

  const handleSave: any = async () => {
    const currentSelectedIds = selectedFamilies.map((f) => f.id);

    const toAdd = currentSelectedIds.filter((id) => !initialSelectedIds.includes(id));
    const toRemove = initialSelectedIds.filter((id) => !currentSelectedIds.includes(id));

    if (toAdd.length === 0 && toRemove.length === 0) return;

    setSaving(true);
    try {
      if (toAdd.length > 0) {
        await FamilyApi.AssignToLine({ lineId: lineId!, familyIds: toAdd });
      }
      if (toRemove.length > 0) {
        await FamilyApi.RemoveFromLine({ lineId: lineId!, familyIds: toRemove });
      }

      displayToast("success", t("form:success"), t("form:failuresLinkUpdated") as string, false, 3000);
      navigate("/lines");
    } catch (err: any) {
      const msg = err?.response?.data?.data ?? err?.message ?? "Error";
      displayToast("error", t("form:error") as string, msg, false, 7000);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges: any = () => {
    const currentIds = selectedFamilies.map((f) => f.id).sort();
    const initialIds = [...initialSelectedIds].sort();
    return JSON.stringify(currentIds) !== JSON.stringify(initialIds);
  };

  return (
    <FormCard
      title={`${t("form:linkFamilies") || "Vincular Famílias"} ${lineName ? `- ${lineName}` : ""}`}
      description={t("form:assignFamiliesDescription") || "Vincular famílias de produtos a esta linha de produção"}
      onSubmit={handleSave}
      onCancel={handleCancel}
      submitButtonLabel={t("form:save") || "Salvar"}
      cancelButtonLabel={t("form:cancel") || "Cancelar"}
      submitDisabled={!hasChanges() || saving}
      loading={saving}
      numColumns={1}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <ConfirmDialog />
        
        <FilterPanel
          title={t("general:filters") || "Filtros"}
          showFilterIcon
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={t("form:name")}
            placeholder={t("form:searchByName") || "Pesquisar por nome"}
            value={filterName}
            onChange={(val) => setFilterName(val)}
          />
          <Input
            type="text"
            label={t("form:description")}
            placeholder={t("form:searchByDescription") || "Pesquisar por descrição"}
            value={filterDescription}
            onChange={(val) => setFilterDescription(val)}
          />
          <Input
            type="number"
            label={t("form:code")}
            placeholder={t("form:searchByCode") || "Pesquisar por código"}
            value={filterCode?.toString() || ""}
            onChange={(val) => setFilterCode(val ? Number(val) : null)}
          />
        </FilterPanel>

        <DataTable
          value={families}
          paginated
          first={page}
          rows={rowsPerPage}
          totalRecords={totalRecords}
          onPageChange={handlePage}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          loading={loading}
          emptyMessage={t("form:noRecordsFound") || "Nenhum registro encontrado"}
          rowsPerPageOptions={[10, 20, 50]}
          resizableColumns
        >
          <Column field="name" header={t("form:name")} sortable style={{ minWidth: "12rem" }} />
          <Column field="description" header={t("form:description")} sortable style={{ minWidth: "16rem" }} />
          <Column field="code" header={t("form:code")} sortable style={{ minWidth: "8rem" }} />
          <Column
            header={t("form:link")}
            style={{ textAlign: "center", width: "6rem" }}
            body={(rowData: FamilyRow) => (
              <Checkbox
                inputId={rowData.id}
                checked={selectedFamilies.some((f) => f.id === rowData.id)}
                onChange={(e) => {
                  if (e.checked) {
                    setSelectedFamilies((prev) => [...prev, rowData]);
                  } else {
                    setSelectedFamilies((prev) =>
                      prev.filter((f) => f.id !== rowData.id)
                    );
                  }
                }}
              />
            )}
          />
        </DataTable>
      </div>
    </FormCard>
  );
};

export default AssignFamiliesToLine;